import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    Combobox,
    Field,
    Option,
    Spinner,
    Button,
    makeStyles,
    tokens,
    useId
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { ChatAPI } from '../utils/api';

interface CachedResponse {
    data: string[];
    timestamp: number;
}

interface AuthorizationAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onOptionSelected?: (value: string) => void; // New callback for explicit selection
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    placeholder?: string;
    label?: string; // Add label prop
    className?: string;
    domain?: string; // Domain for autocomplete (e.g., 'authorization', 'complaint')
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    combobox: {
        minWidth: '300px',
    },
    comboboxContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    clearButton: {
        position: 'absolute',
        right: '32px', // Move further left to avoid dropdown arrow
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        minWidth: 'auto',
        width: '20px',
        height: '20px',
        padding: 0,
        backgroundColor: 'transparent',
        border: 'none',
        color: tokens.colorNeutralForeground3,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorNeutralForeground2,
        },
    },
    spinner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: tokens.spacingVerticalS,
    },
    loadingOption: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        padding: tokens.spacingVerticalS,
        fontStyle: 'italic',
        color: tokens.colorNeutralForeground3,
    },
    noResults: {
        padding: tokens.spacingVerticalS,
        fontStyle: 'italic',
        color: tokens.colorNeutralForeground3,
    }
});

const AuthorizationAutocomplete: React.FC<AuthorizationAutocompleteProps> = ({
    value,
    onChange,
    onOptionSelected,
    disabled = false,
    error = false,
    helperText = '',
    placeholder = 'Enter authorization ID...',
    label = 'Authorization ID', // Default label
    domain = 'authorization' // Default to authorization domain
}) => {
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    
    // Debug: log domain changes
    console.log(`[AuthorizationAutocomplete] Component mounted/updated with domain: "${domain}"`);
    const abortControllerRef = useRef<AbortController | null>(null);
    const cacheRef = useRef<Map<string, CachedResponse>>(new Map());
    const debounceTimerRef = useRef<number | null>(null);
    const lastSearchRef = useRef<string>('');
    const requestCounterRef = useRef<number>(0);

    // IDs for accessibility
    const comboboxId = useId('authorization-combobox');
    const styles = useStyles();

    // Clear cache and suggestions when domain changes
    useEffect(() => {
        cacheRef.current.clear();
        setSuggestions([]);
        setSearchError(null);
        lastSearchRef.current = '';
    }, [domain]);

    // Cache configuration
    const CACHE_TTL = 300000; // 5 minutes
    const MIN_SEARCH_LENGTH = 2;
    const DEBOUNCE_DELAY = 500; // Increased from 300ms
    const MAX_RESULTS = 10;

    const searchAuthorizations = useCallback(async (searchTerm: string) => {
        const trimmedTerm = searchTerm.trim();
        
        // Skip if term is too short or hasn't changed
        if (trimmedTerm.length < MIN_SEARCH_LENGTH || trimmedTerm === lastSearchRef.current) {
            return;
        }

        lastSearchRef.current = trimmedTerm;
        const requestId = ++requestCounterRef.current;

        // Check cache first
        const cachedResult = cacheRef.current.get(trimmedTerm);
        const now = Date.now();
        
        if (cachedResult && (now - cachedResult.timestamp < CACHE_TTL)) {
            console.log(`[Autocomplete] Cache hit for "${trimmedTerm}"`);
            setSuggestions(cachedResult.data);
            setSearchError(null);
            return;
        }

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        setLoading(true);
        setSearchError(null);

        try {
            console.log(`[Autocomplete] Searching for "${trimmedTerm}" in domain "${domain}" (request #${requestId})`);
            const startTime = Date.now();
            
            const response = await ChatAPI.searchRecords(
                domain,
                trimmedTerm, 
                MAX_RESULTS, 
                abortControllerRef.current.signal
            );

            const endTime = Date.now();
            console.log(`[Autocomplete] Request #${requestId} completed in ${endTime - startTime}ms`);

            // Only update if this is still the latest request
            if (requestId === requestCounterRef.current) {
                const records = response.records || [];
                setSuggestions(records);
                setSearchError(null);
                
                // Cache the result
                cacheRef.current.set(trimmedTerm, {
                    data: records,
                    timestamp: now
                });

                // Cleanup old cache entries (keep cache size reasonable)
                if (cacheRef.current.size > 100) {
                    const entries = Array.from(cacheRef.current.entries());
                    entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
                    cacheRef.current.clear();
                    entries.slice(0, 50).forEach(([key, value]) => {
                        cacheRef.current.set(key, value);
                    });
                }
            }
        } catch (err: unknown) {
            const isAbortError = err instanceof Error && err.name === 'AbortError';
            if (!isAbortError && requestId === requestCounterRef.current) {
                console.error(`[Autocomplete] Request #${requestId} failed:`, err);
                setSuggestions([]);
                setSearchError('Search failed. Please try again.');
                lastSearchRef.current = '';
            }
        } finally {
            if (requestId === requestCounterRef.current) {
                setLoading(false);
            }
        }
    }, [domain]);

    const debouncedSearch = useCallback((searchTerm: string) => {
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer
        debounceTimerRef.current = window.setTimeout(() => {
            searchAuthorizations(searchTerm);
        }, DEBOUNCE_DELAY);
    }, [searchAuthorizations]);

    // Effect for handling input changes
    useEffect(() => {
        const trimmedValue = value.trim();
        setSearchError(null);
        
        if (trimmedValue.length >= MIN_SEARCH_LENGTH) {
            debouncedSearch(trimmedValue);
        } else {
            setSuggestions([]);
            setLoading(false);
            
            // Cancel any pending requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            // Clear timers
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        }
    }, [value, debouncedSearch]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const handleInputChange = (event: any) => {
        // Handle both typing and clear button clicks
        const newValue = event.target?.value ?? '';
        onChange(newValue);
    };

    const handleClearClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onChange('');
        setOpen(false);
        setSuggestions([]);
        
        // Trigger the onOptionSelected callback with empty string when clearing
        if (typeof onOptionSelected === 'function') {
            onOptionSelected('');
        }
    };

    const handleOptionSelect = (_event: any, data: any) => {
        if (data.optionValue) {
            onChange(data.optionValue);
            setOpen(false);
            
            // Add trigger for selected option
            if (typeof onOptionSelected === 'function') {
                onOptionSelected(data.optionValue);
            }
        }
    };

    const handleOpenChange = (_event: any, data: any) => {
        setOpen(data.open);
        if (data.open && value.trim().length >= MIN_SEARCH_LENGTH && suggestions.length === 0) {
            debouncedSearch(value.trim());
        }
    };

    // Helper function to get display options
    const getDisplayOptions = () => {
        const trimmedValue = value.trim();
        
        if (loading) {
            return [
                <Option key="loading" value="" disabled text="Searching..." className={styles.loadingOption}>
                    <Spinner size="tiny" /> Searching...
                </Option>
            ];
        }

        if (trimmedValue.length < MIN_SEARCH_LENGTH) {
            const text = `Type at least ${MIN_SEARCH_LENGTH} characters to search`;
            return [
                <Option key="min-length" value="" disabled text={text} className={styles.noResults}>
                    {text}
                </Option>
            ];
        }

        if (searchError) {
            return [
                <Option key="search-error" value="" disabled text={searchError} className={styles.noResults}>
                    {searchError}
                </Option>
            ];
        }

        if (suggestions.length === 0) {
            const text = "No authorization IDs found";
            return [
                <Option key="no-results" value="" disabled text={text} className={styles.noResults}>
                    {text}
                </Option>
            ];
        }

        return suggestions.map((authId) => (
            <Option key={authId} value={authId} text={authId}>
                {authId}
            </Option>
        ));
    };

    return (
        <div className={styles.root}>
            <Field
                label={label}
                validationMessage={error ? helperText : undefined}
                validationState={error ? 'error' : 'none'}
                hint={!error && helperText ? helperText : undefined}
            >
                <div className={styles.comboboxContainer}>
                    <Combobox
                        id={comboboxId}
                        className={styles.combobox}
                        placeholder={placeholder}
                        value={value}
                        selectedOptions={value ? [value] : []}
                        onInput={handleInputChange}
                        onChange={handleInputChange}
                        onOptionSelect={handleOptionSelect}
                        onOpenChange={handleOpenChange}
                        open={open}
                        disabled={disabled}
                        freeform
                        autoComplete="off"
                    >
                        {getDisplayOptions()}
                    </Combobox>
                    {value && (
                        <Button
                            className={styles.clearButton}
                            icon={<DismissRegular />}
                            onClick={handleClearClick}
                            disabled={disabled}
                            title="Clear input"
                            appearance="transparent"
                            size="small"
                        />
                    )}
                </div>
            </Field>
        </div>
    );
};

export default AuthorizationAutocomplete;